import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { FaBolt } from 'react-icons/fa';
import { LoginContext } from '../../Contexts/LoginContext';
import { formatCurrency, formatCurrencyDual } from '../../data/constants';
import { showToast } from '../utils/ShowToast';
import Loader from '../utils/Loader';

const API = process.env.REACT_APP_Backend_Host_Name;

/**
 * Buy or extend a promotion window on one listing.
 *
 * Mirrors the membership flow in Upgrade.js: credits are applied first, and a
 * Stripe intent is only opened for whatever is left. Pricing and tax are
 * returned by the server rather than computed here, so what the seller sees is
 * exactly what they will be charged.
 */
const BoostModal = ({ post, activeBoost, onClose, onBoosted }) => {
    const { currency, exchangeRate } = useContext(LoginContext);
    const stripe = useStripe();
    const elements = useElements();

    const [packages, setPackages] = useState([]);
    const [selectedDays, setSelectedDays] = useState(null);
    const [quote, setQuote] = useState(null);
    const [balance, setBalance] = useState(0);
    const [applyCredits, setApplyCredits] = useState(true);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const token = localStorage.getItem('token');
    const isExtending = Boolean(activeBoost);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const [pkgRes, balRes] = await Promise.allSettled([
                    axios.get(`${API}/api/v1/boosts/packages`),
                    axios.get(`${API}/api/v1/payments/buyer/balance`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                if (cancelled) return;

                if (pkgRes.status === 'fulfilled' && pkgRes.value.data.success) {
                    const list = pkgRes.value.data.packages || [];
                    setPackages(list);
                    if (list.length) setSelectedDays(list[0].days);
                }
                if (balRes.status === 'fulfilled') {
                    setBalance(balRes.value.data?.balance ?? 0);
                }
            } catch (err) {
                if (!cancelled) showToast('Could not load boost options. Please try again.', 'error');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [token]);

    const selectedPackage = useMemo(
        () => packages.find((p) => p.days === selectedDays) || null,
        [packages, selectedDays]
    );

    // Tax depends on the seller's address, so the total is quoted by the server
    // rather than guessed here.
    const price = selectedPackage?.price ?? 0;
    const tax = quote?.tax ?? 0;
    const total = quote?.totalPaid ?? price;
    const creditToApply = applyCredits && balance > 0 ? Math.min(balance, total) : 0;
    const cardDue = Math.max(0, total - creditToApply);
    const paysWithCreditsOnly = total > 0 && creditToApply >= total;

    const formatDate = (d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

    const newEndDate = useMemo(() => {
        if (!selectedPackage) return null;
        // Extending stacks on whatever is left, matching the server.
        const base = isExtending && new Date(activeBoost.endDate) > new Date()
            ? new Date(activeBoost.endDate)
            : new Date();
        return new Date(base.getTime() + selectedPackage.days * 86400000);
    }, [selectedPackage, isExtending, activeBoost]);

    const handleBoost = async () => {
        if (!selectedPackage) return;
        setSubmitting(true);

        try {
            const { data: intent } = await axios.post(
                `${API}/api/v1/boosts/create-intent`,
                { listingId: post.id, isB2B: post.isB2B, days: selectedPackage.days, creditAmount: creditToApply },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            let paymentIntentId = null;

            if (!intent.fullCredits) {
                if (!stripe || !elements) {
                    showToast('Payment form is still loading. Please try again in a moment.', 'error');
                    setSubmitting(false);
                    return;
                }
                const cardElement = elements.getElement(CardNumberElement);
                if (!cardElement) {
                    showToast('Please enter your card details.', 'error');
                    setSubmitting(false);
                    return;
                }

                const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({ type: 'card', card: cardElement });
                if (pmError) { showToast(pmError.message, 'error'); setSubmitting(false); return; }

                const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(intent.clientSecret, {
                    payment_method: paymentMethod.id,
                });
                if (confirmError) { showToast(confirmError.message, 'error'); setSubmitting(false); return; }
                if (paymentIntent.status !== 'succeeded') {
                    showToast('Payment did not complete. Please try again.', 'error');
                    setSubmitting(false);
                    return;
                }
                paymentIntentId = paymentIntent.id;
            }

            const { data } = await axios.post(
                `${API}/api/v1/boosts/confirm`,
                {
                    listingId: post.id,
                    isB2B: post.isB2B,
                    days: selectedPackage.days,
                    paymentIntentId,
                    creditAmount: intent.appliedCreditAmount ?? creditToApply,
                    fullCredits: intent.fullCredits,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (data.success) {
                showToast(isExtending ? 'Your boost has been extended!' : 'Your listing is now boosted!', 'success');
                onBoosted?.(data.boost);
                onClose();
            }
        } catch (err) {
            showToast(
                err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Could not complete the boost.',
                'error'
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Ask the server to price the selected package so tax is never guessed.
    // Uses /quote rather than /create-intent so switching duration doesn't leave
    // an abandoned Stripe PaymentIntent behind on every click.
    useEffect(() => {
        if (!selectedPackage) { setQuote(null); return; }
        let cancelled = false;

        const priceIt = async () => {
            try {
                const { data } = await axios.post(
                    `${API}/api/v1/boosts/quote`,
                    { listingId: post.id, isB2B: post.isB2B, days: selectedPackage.days },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (cancelled) return;
                setQuote({ price: data.price, tax: data.tax, totalPaid: data.totalPaid });
                if (typeof data.creditBalance === 'number') setBalance(data.creditBalance);
            } catch {
                // Fall back to the untaxed package price rather than blocking the
                // modal; the server still charges the correct total either way.
                if (!cancelled) setQuote(null);
            }
        };

        priceIt();
        return () => { cancelled = true; };
    }, [selectedPackage, post.id, post.isB2B, token]);

    return (
        <div className="modalOverlay paymentModelOverlayDiv" onClick={() => !submitting && onClose()}>
            <div className="modalContent boostModal" onClick={(e) => e.stopPropagation()}>
                <div className="form">
                    <h2 className="primaryHeadingSmall">
                        <span>{isExtending ? 'Extend boost' : 'Boost this listing'}</span>
                    </h2>

                    <div className="boostModalListing">
                        {post.product?.image && <img src={post.product.image} alt={post.product.name} className="boostModalThumb" />}
                        <div>
                            <p className="boostModalListingName">{post.product?.name}</p>
                            {isExtending && (
                                <p className="boostModalActiveNote">
                                    Currently boosted until <strong>{formatDate(activeBoost.endDate)}</strong>
                                </p>
                            )}
                        </div>
                    </div>

                    <p className="boostModalIntro">
                        Boosted listings appear <strong>above all other listings</strong> in search results and category
                        pages — ahead of both Premium and Basic sellers.
                    </p>

                    <div className="horizontalLine"></div>

                    {loading ? (
                        <Loader type="miniPlus" />
                    ) : packages.length === 0 ? (
                        <p className="boostModalEmpty">Listing boosts are not available right now. Please check back later.</p>
                    ) : (
                        <>
                            <label className="boostModalLabel">Choose a duration</label>
                            <div className="boostPackageGrid">
                                {packages.map((pkg) => (
                                    <button
                                        type="button"
                                        key={pkg.days}
                                        className={`boostPackageCard ${selectedDays === pkg.days ? 'selected' : ''}`}
                                        onClick={() => setSelectedDays(pkg.days)}
                                        disabled={submitting}
                                    >
                                        <FaBolt className="boostPackageIcon" />
                                        <span className="boostPackageDays">{pkg.days} days</span>
                                        <span className="boostPackagePrice">{formatCurrency(pkg.price, currency, exchangeRate)}</span>
                                    </button>
                                ))}
                            </div>

                            {selectedPackage && (
                                <>
                                    <div className="row">
                                        <span>Boost price</span>
                                        <strong>{formatCurrency(price, currency, exchangeRate)}</strong>
                                    </div>
                                    {tax > 0 && (
                                        <div className="row">
                                            <span>Tax</span>
                                            <strong>{formatCurrency(tax, currency, exchangeRate)}</strong>
                                        </div>
                                    )}
                                    <div className="row">
                                        <span>Total</span>
                                        <strong>{formatCurrency(total, currency, exchangeRate)}</strong>
                                    </div>
                                    {newEndDate && (
                                        <div className="row">
                                            <span>{isExtending ? 'New end date' : 'Boosted until'}</span>
                                            <strong>{formatDate(newEndDate)}</strong>
                                        </div>
                                    )}

                                    {balance > 0 && (
                                        <div className="boostModalCredits">
                                            <label className="checkoutCreditsApplyRow">
                                                <input
                                                    type="checkbox"
                                                    checked={applyCredits}
                                                    onChange={(e) => setApplyCredits(e.target.checked)}
                                                    disabled={submitting}
                                                />
                                                <span>
                                                    Apply account credits ({formatCurrencyDual(balance, currency, exchangeRate)} available)
                                                    {applyCredits && creditToApply > 0 && (
                                                        <span className="checkoutCreditsApplyHint">
                                                            {' '}— {formatCurrencyDual(creditToApply, currency, exchangeRate)} will be used
                                                        </span>
                                                    )}
                                                </span>
                                            </label>
                                            {applyCredits && paysWithCreditsOnly && (
                                                <p className="checkoutCreditsOnlyNote">
                                                    This boost will be covered entirely from account credits. No card charge.
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {!paysWithCreditsOnly && (
                                        <div className="checkoutStripeFields">
                                            <div className="checkoutStripeRow">
                                                <label>Card Number</label>
                                                <CardNumberElement className="inputField checkoutStripeElement" options={{ style: { base: { fontSize: '16px' } } }} />
                                            </div>
                                            <div className="checkoutStripeRowGroup">
                                                <div className="checkoutStripeRow">
                                                    <label>Expiry</label>
                                                    <CardExpiryElement className="inputField checkoutStripeElement" options={{ style: { base: { fontSize: '16px' } } }} />
                                                </div>
                                                <div className="checkoutStripeRow">
                                                    <label>CVC</label>
                                                    <CardCvcElement className="inputField checkoutStripeElement" options={{ style: { base: { fontSize: '16px' } } }} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>

                <div className="buttonsDiv">
                    <button className="modalButton cancel" onClick={onClose} disabled={submitting}>
                        Close
                    </button>
                    <button
                        className="modalButton submit"
                        onClick={handleBoost}
                        disabled={submitting || loading || !selectedPackage}
                    >
                        {submitting ? <Loader type="miniPlus" /> : (
                            paysWithCreditsOnly
                                ? 'Activate with credits'
                                : `Pay ${formatCurrency(cardDue, currency, exchangeRate)}`
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BoostModal;
